import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Chip, Divider, IconButton, TextField, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import type {
    EmbedProviderType,
    SetProviderRequest,
    SpConfig,
    SsoTestResult
} from "../../../common/interfaces/admin/integration.interface";
import { signInForSsoTest } from "../../../common/utilities/sso/sso-test-signin";
import "./style.scss";

/** The part of a provider record this control reads. Both the embed
 *  integration and the tenant SSO provider (Phase 4) have these fields. */
export interface SsoProviderState {
    firebaseTenantId: string | null;
    firebaseProviderId: string | null;
    providerConfiguredAt: string | null;
    lastSsoTestAt: string | null;
    lastSsoTestOutcome: string | null;
}

/** The endpoints behind the control — integration- or tenant-scoped. */
export interface SsoProviderConfigApi {
    fetchSpConfig(): Promise<{ data: unknown }>;
    fetchSpMetadataXml(): Promise<{ data: unknown }>;
    setProvider(body: SetProviderRequest): Promise<{ data: unknown }>;
    testSso(idToken: string): Promise<{ data: SsoTestResult | undefined }>;
}

export interface SsoProviderConfigProps {
    providerType: EmbedProviderType;
    state: SsoProviderState;
    /**
     * Origin of the embed host (`https://chat.<env>`), the one Firebase-
     * authorized page that can run the tenant-scoped test sign-in. null when
     * it cannot be derived; the test button then explains.
     */
    chatOrigin: string | null;
    metadataFileName: string;
    api: SsoProviderConfigApi;
    /** The record the setProvider endpoint returned. */
    onConfigured(updated: unknown): void;
    /** Test outcome, for the caller to merge into its record. */
    onTested(result: SsoTestResult): void;
    /** Copy under the test status; what "enabled/activated" means here. */
    passedHint: string;
    pendingHint: string;
}

/** Pull every PEM certificate block out of a textarea; fall back to one entry. */
export function parseCertificates(raw: string): string[] {
    const blocks = raw.match(/-----BEGIN CERTIFICATE-----[\s\S]*?-----END CERTIFICATE-----/g);
    if (blocks && blocks.length > 0) return blocks.map(b => b.trim());
    const trimmed = raw.trim();
    return trimmed ? [trimmed] : [];
}

function CopyField({ label, value }: { label: string; value: string }) {
    const [copied, setCopied] = useState(false);
    return (
        <div className="sso-provider-config__copy-row">
            <TextField
                label={label}
                value={value}
                InputProps={{ readOnly: true }}
                fullWidth
                size="small"
            />
            <Tooltip title={copied ? "Copied" : "Copy"}>
                <IconButton
                    aria-label={`Copy ${label}`}
                    onClick={() => {
                        void navigator.clipboard?.writeText(value);
                        setCopied(true);
                        window.setTimeout(() => setCopied(false), 1500);
                    }}
                >
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </div>
    );
}

/**
 * SP details → IdP details → test, for one SAML/OIDC provider on a company's
 * Firebase tenant. Shared by the embed integration editor and the tenant
 * Authentication tab (tenant_authentication_plan.md §2.4); the caller says
 * which endpoints back it. IdP secrets are write-only: the form is always
 * blank on load because the backend never returns them.
 */
export default function SsoProviderConfig({
    providerType,
    state,
    chatOrigin,
    metadataFileName,
    api,
    onConfigured,
    onTested,
    passedHint,
    pendingHint
}: SsoProviderConfigProps) {
    const isSaml = providerType === "saml";

    const [spConfig, setSpConfig] = useState<SpConfig | null>(null);
    const [spError, setSpError] = useState<string | null>(null);

    const [idpEntityId, setIdpEntityId] = useState("");
    const [ssoURL, setSsoURL] = useState("");
    const [certs, setCerts] = useState("");
    const [clientId, setClientId] = useState("");
    const [issuer, setIssuer] = useState("");
    const [clientSecret, setClientSecret] = useState("");

    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [testError, setTestError] = useState<string | null>(null);

    const { fetchSpConfig, fetchSpMetadataXml } = api;
    useEffect(() => {
        let cancelled = false;
        setSpConfig(null);
        setSpError(null);
        fetchSpConfig()
            .then(({ data }) => {
                if (!cancelled) setSpConfig(data as SpConfig);
            })
            .catch(err => {
                if (!cancelled)
                    setSpError(err?.response?.data?.message ?? "Couldn't load SP config.");
            });
        return () => {
            cancelled = true;
        };
    }, [fetchSpConfig]);

    const downloadMetadata = useCallback(async () => {
        try {
            const { data } = await fetchSpMetadataXml();
            const blob = new Blob([data as string], { type: "application/xml" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = metadataFileName;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err: any) {
            setSpError(err?.response?.data?.message ?? "Metadata download failed.");
        }
    }, [fetchSpMetadataXml, metadataFileName]);

    const saveProvider = async () => {
        setSaving(true);
        setFormError(null);
        try {
            const body: SetProviderRequest = isSaml
                ? {
                      idpEntityId: idpEntityId.trim(),
                      ssoURL: ssoURL.trim(),
                      x509Certificates: parseCertificates(certs)
                  }
                : {
                      clientId: clientId.trim(),
                      issuer: issuer.trim(),
                      clientSecret: clientSecret.trim()
                  };
            const { data } = await api.setProvider(body);
            onConfigured(data);
            // Clear the secret-bearing fields once accepted; they're in Firebase now.
            setCerts("");
            setClientSecret("");
        } catch (err: any) {
            setFormError(err?.response?.data?.message ?? "Saving the provider failed.");
        } finally {
            setSaving(false);
        }
    };

    const runTest = async () => {
        if (!state.firebaseTenantId || !state.firebaseProviderId) return;
        if (!chatOrigin) {
            setTestError("Can't determine the embed host that runs the test sign-in.");
            return;
        }
        setTesting(true);
        setTestError(null);
        try {
            const idToken = await signInForSsoTest({
                chatOrigin,
                tenantId: state.firebaseTenantId,
                providerId: state.firebaseProviderId,
                providerType
            });
            const { data } = await api.testSso(idToken);
            if (data?.outcome === "failed") {
                setTestError(data?.error ?? "The IdP sign-in did not verify against this tenant.");
            }
            onTested({
                outcome: data?.outcome ?? "failed",
                testedAt: data?.testedAt ?? new Date().toISOString(),
                error: data?.error
            });
        } catch (err: any) {
            // Firebase popup errors (closed/blocked) or network — surface the code.
            setTestError(
                err?.code ? `Sign-in error: ${err.code}` : (err?.message ?? "Test failed.")
            );
        } finally {
            setTesting(false);
        }
    };

    const providerConfigured = !!state.providerConfiguredAt;
    const testPassed = state.lastSsoTestOutcome === "passed";

    return (
        <div className="sso-provider-config">
            {/* --- 1. SP-side values for the customer's IdP team ------------- */}
            <section className="sso-provider-config__section">
                <h3>1 · Give these to the IdP team</h3>
                {spError && <Alert severity="error">{spError}</Alert>}
                {spConfig?.type === "saml" && (
                    <>
                        <CopyField label="SP Entity ID" value={spConfig.spEntityId} />
                        <CopyField label="ACS URL (Reply URL)" value={spConfig.acsUrl} />
                        <CopyField label="Name ID format" value={spConfig.nameIdFormat} />
                        <Button onClick={downloadMetadata} size="small">
                            Download SP metadata XML
                        </Button>
                    </>
                )}
                {spConfig?.type === "oidc" && (
                    <CopyField label="Redirect URI" value={spConfig.redirectUri} />
                )}
            </section>

            <Divider />

            {/* --- 2. IdP details (→ Firebase, never stored) ---------------- */}
            <section className="sso-provider-config__section">
                <h3>2 · Enter the IdP details</h3>
                <Alert severity="info">
                    These are sent to Firebase and never stored by MBAssist. Saving (or changing)
                    them clears any previous test result — you must re-test before enabling.
                </Alert>
                {formError && <Alert severity="error">{formError}</Alert>}
                {isSaml ? (
                    <>
                        <TextField
                            label="IdP Entity ID (Issuer)"
                            value={idpEntityId}
                            onChange={e => setIdpEntityId(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="IdP SSO URL (https)"
                            value={ssoURL}
                            onChange={e => setSsoURL(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="X.509 signing certificate(s) — PEM"
                            value={certs}
                            onChange={e => setCerts(e.target.value)}
                            placeholder={
                                "-----BEGIN CERTIFICATE-----\n…\n-----END CERTIFICATE-----"
                            }
                            multiline
                            minRows={4}
                            maxRows={8}
                            fullWidth
                        />
                    </>
                ) : (
                    <>
                        <TextField
                            label="OIDC Client ID"
                            value={clientId}
                            onChange={e => setClientId(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="OIDC Issuer URL (https)"
                            value={issuer}
                            onChange={e => setIssuer(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="OIDC Client secret"
                            value={clientSecret}
                            onChange={e => setClientSecret(e.target.value)}
                            type="password"
                            fullWidth
                        />
                    </>
                )}
                <Button variant="contained" onClick={saveProvider} disabled={saving}>
                    {saving ? "Saving…" : providerConfigured ? "Update provider" : "Save provider"}
                </Button>
            </section>

            <Divider />

            {/* --- 3. Test before enabling ---------------------------------- */}
            <section className="sso-provider-config__section">
                <h3>3 · Test the sign-in</h3>
                {!providerConfigured ? (
                    <Alert severity="info">Save the provider details above first.</Alert>
                ) : (
                    <>
                        <div className="sso-provider-config__test-status">
                            {state.lastSsoTestOutcome == null ? (
                                <Chip label="Not tested yet" />
                            ) : testPassed ? (
                                <Chip color="success" label="Last test: passed" />
                            ) : (
                                <Chip color="error" label="Last test: failed" />
                            )}
                            {state.lastSsoTestAt && (
                                <span className="sso-provider-config__test-when">
                                    {new Date(state.lastSsoTestAt).toLocaleString()}
                                </span>
                            )}
                        </div>
                        {testError && <Alert severity="error">{testError}</Alert>}
                        <Button variant="outlined" onClick={runTest} disabled={testing}>
                            {testing ? "Waiting for sign-in…" : "Test SSO sign-in"}
                        </Button>
                        {testPassed ? (
                            <Alert severity="success">{passedHint}</Alert>
                        ) : (
                            <Alert severity="warning">{pendingHint}</Alert>
                        )}
                    </>
                )}
            </section>
        </div>
    );
}
